import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import {
  RewardCardDialog,
  Props as RewardCardDialogProps,
} from '@sorare/gameplay/src/components/rewards/RewardCardDialog';

import late from 'components/onboarding/MLBOnboarding/assets/late.jpg';

import { FirstWeekClaim } from './FirstWeekClaim';
import {
  MLBDailyClaimDialogQuery,
  MLBDailyClaimDialogQueryVariables,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  padding: var(--triple-unit) 0;
  flex: 1;
  height: 100%;
  min-height: min(calc(100vh - var(--unit) * 8), 800px);

  /* First child is the dialog close button */
  & > *:not(:last-child):not(:first-child) {
    border-bottom: 1px solid var(--c-nd-200);
    padding-bottom: var(--triple-unit);
  }

  background-image: linear-gradient(to top, var(--c-black), transparent 50%),
    url(${late});
  background-size: cover;
  background-position: top;
  background-repeat: no-repeat;
`;

const CLAIM_TICKET_QUERY = gql`
  query MLBDailyClaimDialogQuery {
    currentUser {
      slug
      baseballUserProfile {
        id
        dailyClaimTasks {
          id
          ...FirstWeekClaim_mlbManagerTask
        }
      }
    }
  }
  ${FirstWeekClaim.fragments.mlbManagerTask}
` as TypedDocumentNode<
  MLBDailyClaimDialogQuery,
  MLBDailyClaimDialogQueryVariables
>;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const DailyClaimDialog = ({ open, onClose }: Props) => {
  const { data, loading } = useQuery(CLAIM_TICKET_QUERY, { skip: !open });
  const currentUser = data?.currentUser;

  const [rewardCardDialogProps, setRewardCardDialogProps] = useState<{
    ftue: boolean;
    card: RewardCardDialogProps['card'];
  } | null>(null);

  if (!currentUser?.baseballUserProfile) {
    return null;
  }

  const hasClaimTasks =
    (currentUser.baseballUserProfile?.dailyClaimTasks || []).length > 0;

  return (
    <>
      {hasClaimTasks && (
        <Dialog
          open={open}
          maxWidth="xs"
          fullWidth
          hideHeader
          onClose={onClose}
        >
          <Skeleton loading={loading}>
            <Root gap={3}>
              {currentUser.baseballUserProfile.dailyClaimTasks.length > 0 && (
                <FirstWeekClaim
                  dailyClaimTasks={
                    currentUser.baseballUserProfile.dailyClaimTasks
                  }
                  onClose={onClose}
                />
              )}
            </Root>
          </Skeleton>
        </Dialog>
      )}
      <RewardCardDialog
        ftue={rewardCardDialogProps?.ftue || false}
        card={rewardCardDialogProps?.card}
        open={!!rewardCardDialogProps}
        onClose={() => setRewardCardDialogProps(null)}
      />
    </>
  );
};
